"use client"

import * as React from "react"
import { PencilSimpleIcon } from "@phosphor-icons/react"
import { cn } from "cn"

import { Button } from "./button"
import { Textarea } from "./textarea"

function EditableText({
  value,
  onSave,
  placeholder,
  meta,
  leading,
  trailing,
  inline = false,
  editLabel = "Edit",
  autoFocus = false,
  className,
  ...props
}: Omit<React.ComponentProps<"div">, "onChange" | "autoFocus"> & {
  value: string
  onSave: (value: string) => void
  placeholder?: string
  /** Shown under the text once there is text, e.g. where it came from. */
  meta?: React.ReactNode
  /** Shown right before the text, on the same line, and not edited with it, e.g. a label. */
  leading?: React.ReactNode
  /** Shown right after the text, on the same line. */
  trailing?: React.ReactNode
  /**
   * Edited in place, the way a quiet field is: the text itself takes the cursor, with a soft highlight; Enter or
   * leaving it saves, Escape puts it back. No textarea, no Save and Cancel.
   */
  inline?: boolean
  editLabel?: string
  /** inline: takes the cursor when it appears, at the end of the text, e.g. when the user came back to edit it. */
  autoFocus?: boolean
}) {
  const [editing, setEditing] = React.useState(false)
  const inlineField = React.useRef<HTMLSpanElement>(null)
  React.useEffect(() => {
    const field = inlineField.current
    if (!autoFocus || !field) return
    field.focus()
    const range = document.createRange()
    range.selectNodeContents(field)
    range.collapse(false)
    window.getSelection()?.removeAllRanges()
    window.getSelection()?.addRange(range)
  }, [autoFocus])

  const [draft, setDraft] = React.useState(value)

  if (inline) {
    return (
      <div
        data-slot="editable-text"
        className={cn("relative", className)}
        {...props}
      >
        {leading}
        <span
          ref={inlineField}
          key={value}
          role="textbox"
          aria-label={editLabel}
          contentEditable="plaintext-only"
          suppressContentEditableWarning
          spellCheck
          onBlur={(event) => {
            const next = event.currentTarget.textContent?.trim() ?? ""
            if (!next) event.currentTarget.textContent = value
            else if (next !== value) onSave(next)
          }}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault()
              event.currentTarget.blur()
            }
            if (event.key === "Escape") {
              event.currentTarget.textContent = value
              event.currentTarget.blur()
            }
          }}
          className="-mx-1 cursor-text rounded-md px-1 transition-colors outline-none hover:bg-foreground/[0.04] focus:bg-foreground/[0.05]"
        >
          {value}
        </span>
        {value && trailing}
      </div>
    )
  }

  const start = () => {
    setDraft(value)
    setEditing(true)
  }

  const save = () => {
    const next = draft.trim()
    if (next && next !== value) onSave(next)
    setEditing(false)
  }

  if (editing) {
    return (
      <div
        data-slot="editable-text"
        data-editing=""
        className={cn("flex flex-col gap-2", className)}
        {...props}
      >
        <Textarea
          ref={(element) => element?.focus({ preventScroll: true })}
          value={draft}
          placeholder={placeholder}
          aria-label={editLabel}
          rows={1}
          className="field-sizing-content min-h-10 resize-none [font-family:inherit] text-[length:inherit] leading-[inherit]"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault()
              save()
            }
            if (event.key === "Escape") setEditing(false)
          }}
        />
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={!draft.trim()} onClick={save}>
            Save
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div
      data-slot="editable-text"
      className={cn("group/editable relative pe-8", className)}
      {...props}
    >
      {leading}
      <span
        title="Click to edit"
        onClick={start}
        className="cursor-text rounded-sm transition-colors hover:bg-muted"
      >
        {value || <span className="text-muted-foreground">{placeholder}</span>}
      </span>
      {value && trailing}
      {value && meta && (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          {meta}
        </div>
      )}
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={editLabel}
        onClick={start}
        className="absolute end-0 top-0 text-muted-foreground opacity-0 transition-opacity group-hover/editable:opacity-100 focus-visible:opacity-100"
      >
        <PencilSimpleIcon />
      </Button>
    </div>
  )
}

export { EditableText }
